import * as Sentry from '@sentry/react';
import { SnapshotIn } from 'mobx-state-tree';
import { DashboardSections } from '../models';
import {
  tabsMenuFirstLevel,
  tabsMenuSecondLevel,
} from 'views/authorized/pages/main/components/menu-tabs/tabs-list';
import type { DashboardAdminSectionKey, DashboardSectionKey } from '../types';

const dashboardSectionKeys = new Set(Object.keys(DashboardSections.properties));

export const DASHBOARD_ADMIN_SECTIONS: readonly DashboardAdminSectionKey[] = [
  'providers',
  'client_errors',
];

// Статичный каталог готовим один раз; порядок и вложенность берём из меню.
const sectionPermissions = tabsMenuFirstLevel.map(
  ({ slug, linkUrl, routeName }) => {
    const childSlugs = tabsMenuSecondLevel
      .filter((child) => child.parent_slug === slug)
      .map((child) => child.slug);
    const hasOwnPage = !!(linkUrl || routeName);

    return {
      slug,
      // У группы право может быть выдано на сам раздел или его подразделы.
      providerSlugs: hasOwnPage ? [slug] : [slug, ...childSlugs],
      // Для перехода в группу сотруднику нужен доступ хотя бы к одному пункту.
      userSlugs: hasOwnPage ? [slug] : childSlugs,
    };
  }
);

export const dashboardSectionPermissions = sectionPermissions.filter(
  (section): section is typeof section & { slug: DashboardSectionKey } =>
    dashboardSectionKeys.has(section.slug)
);

export const dashboardAdminSectionPermissions = sectionPermissions.filter(
  (section): section is typeof section & { slug: DashboardAdminSectionKey } =>
    DASHBOARD_ADMIN_SECTIONS.some((slug) => slug === section.slug)
);

/** Равные счётчики сохраняют исходный порядок меню. */
export const sortDashboardSectionsByVisits = (
  sections: readonly DashboardSectionKey[],
  visits: Pick<ReadonlyMap<string, number>, 'get'>
): DashboardSectionKey[] =>
  [...sections].sort(
    (first, second) => (visits.get(second) ?? 0) - (visits.get(first) ?? 0)
  );

/* Сверка на случай если в запросе поменяются ключи */
export const validateDashboardSections = (
  response: SnapshotIn<typeof DashboardSections>
) => {
  const responseKeys = new Set(Object.keys(response));
  const missingKeys = Array.from(dashboardSectionKeys).filter(
    (key) => !responseKeys.has(key)
  );
  const unexpectedKeys = Array.from(responseKeys).filter(
    (key) => !dashboardSectionKeys.has(key)
  );

  if (!missingKeys.length && !unexpectedKeys.length) {
    return;
  }

  Sentry.captureException(
    new Error('Ключи запроса getDashboardSections перестали соответствовать'),
    {
      level: 'error',
      tags: { request: 'getDashboardSections' },
      extra: { missingKeys, unexpectedKeys },
      fingerprint: ['dashboard-sections-keys-mismatch'],
    }
  );
};
